import React, { useEffect, useMemo, useRef } from "react";
import { format } from "date-fns";
import { Pressable, ScrollView, StyleSheet, Text, View } from "react-native";
import { Calendar } from "react-native-paper-dates";
import { MD3LightTheme, Provider as PaperProvider } from "react-native-paper";
import {
  getAvailableMeetingSlotsForLocalDate,
  getMeetingTimeParts,
} from "../../../utils/meetingAvailability";
import { useAppSelector } from "../../../redux/store";

interface DateTimePickerProps {
  selectedDate: Date;
  selectedHour: string;
  selectedMinute: string;
  selectedAmPm: string;
  onDateChange: (date: Date) => void;
  onHourChange: (hour: string) => void;
  onMinuteChange: (minute: string) => void;
  onAmPmChange: (ampm: string) => void;
  variant?: "desktop" | "mobile";
}

export default function DateTimePicker({
  selectedDate,
  selectedHour,
  selectedMinute,
  selectedAmPm,
  onDateChange,
  onHourChange,
  onMinuteChange,
  onAmPmChange,
  variant = "desktop",
}: DateTimePickerProps) {
  const isMobile = variant === "mobile";
  const timeScrollRef = useRef<ScrollView>(null);
  // QA/test accounts (#90) may schedule at any time; everyone else is limited
  // to the fixed Eastern-time windows.
  const isTestAccount = useAppSelector((state) => state.user.testAccount);
  const timeOptions = useMemo(
    () =>
      getAvailableMeetingSlotsForLocalDate(selectedDate, new Date(), {
        unrestricted: isTestAccount,
      }).map((slot) => {
        const parts = getMeetingTimeParts(slot);
        return {
          ...parts,
          key: slot.toISOString(),
          label: `${parts.hour}:${parts.minute} ${parts.ampm}`,
        };
      }),
    [isTestAccount, selectedDate],
  );
  const selectedTimeIndex = timeOptions.findIndex(
    (option) =>
      option.hour === selectedHour &&
      option.minute === selectedMinute &&
      option.ampm === selectedAmPm,
  );

  useEffect(() => {
    if (selectedTimeIndex >= 0 || timeOptions.length === 0) {
      return;
    }

    const [nextTime] = timeOptions;
    onHourChange(nextTime.hour);
    onMinuteChange(nextTime.minute);
    onAmPmChange(nextTime.ampm);
  }, [
    onAmPmChange,
    onHourChange,
    onMinuteChange,
    selectedTimeIndex,
    timeOptions,
  ]);

  useEffect(() => {
    const scrollTo = () => {
      if (timeScrollRef.current && selectedTimeIndex >= 0) {
        timeScrollRef.current.scrollTo({
          y: selectedTimeIndex * 40,
          animated: true,
        });
      }
    };

    const timer = setTimeout(scrollTo, 100);
    return () => clearTimeout(timer);
  }, [selectedTimeIndex]);

  const formatTime = () => {
    if (selectedTimeIndex >= 0) {
      return timeOptions[selectedTimeIndex].label;
    }

    return timeOptions[0]?.label ?? "No times";
  };

  const handleCalendarChange = (params: any) => {
    if (params?.date instanceof Date) {
      if (params.date.getTime() < todayStart.getTime()) {
        return;
      }

      onDateChange(params.date);
    }
  };

  const todayStart = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }, []);

  const calendarTheme = useMemo(
    () => ({
      ...MD3LightTheme,
      colors: {
        ...MD3LightTheme.colors,
        primary: "#d63c8a",
        primaryContainer: "#d63c8a",
        secondary: "#d63c8a",
        tertiary: "#d63c8a",
        surfaceDisabled: "#d8dbe1",
        onSurfaceDisabled: "#8f8f8f",
      },
    }),
    [],
  );

  return (
    <View style={[styles.container, isMobile && styles.containerMobile]}>
      <View style={[styles.displayRow, isMobile && styles.displayRowMobile]}>
        <View style={[styles.displayBox, isMobile && styles.displayBoxMobile]}>
          <Text
            style={[styles.displayText, isMobile && styles.displayTextMobile]}
          >
            {format(selectedDate, "d MMM yyyy")}
          </Text>
        </View>
        <View style={[styles.displayBox, isMobile && styles.displayBoxMobile]}>
          <Text
            style={[styles.displayText, isMobile && styles.displayTextMobile]}
          >
            {formatTime()}
          </Text>
        </View>
      </View>

      <View
        style={[
          styles.pickerContainerBase,
          isMobile
            ? styles.pickerContainerMobile
            : styles.pickerContainerDesktop,
        ]}
      >
        <View
          style={[
            styles.calendarSection,
            isMobile && styles.calendarSectionMobile,
          ]}
        >
          <PaperProvider theme={calendarTheme}>
            <Calendar
              date={selectedDate}
              mode="single"
              onChange={handleCalendarChange}
              locale="en"
              validRange={{ startDate: todayStart }}
            />
          </PaperProvider>
        </View>

        <View
          style={[styles.timeSection, isMobile && styles.timeSectionMobile]}
        >
          {timeOptions.length > 0 ? (
            <ScrollView
              ref={timeScrollRef}
              style={[styles.timeScroll, isMobile && styles.timeScrollMobile]}
              showsVerticalScrollIndicator={false}
              contentContainerStyle={styles.timeScrollContent}
              snapToInterval={40}
              decelerationRate="fast"
            >
              {timeOptions.map((option, index) => {
                const selected = selectedTimeIndex === index;

                return (
                  <Pressable
                    key={option.key}
                    onPress={() => {
                      onHourChange(option.hour);
                      onMinuteChange(option.minute);
                      onAmPmChange(option.ampm);
                    }}
                    style={[
                      styles.timeOption,
                      selected && styles.timeOptionSelected,
                    ]}
                  >
                    <Text
                      style={[
                        styles.timeOptionText,
                        selected && styles.timeOptionTextSelected,
                      ]}
                    >
                      {option.label}
                    </Text>
                  </Pressable>
                );
              })}
            </ScrollView>
          ) : (
            <View style={styles.emptyTimeState}>
              <Text style={styles.emptyTimeText}>
                No meeting times are available on this date.
              </Text>
            </View>
          )}
        </View>
      </View>
    </View>
  );
}

// react-native-paper-dates renders the single-date calendar in "horizontal"
// mode, which has NO vertical scroll — the month grid is clipped to whatever
// height its container gives it. The old fixed 332px only fit ~3 week rows, so
// the last rows of the month (e.g. 26–31 July) were cut off and unreachable.
// A month can span up to 6 week rows, so size the container for the tallest
// case: calendar header (buttonContainer 56 + margins 12 + day names 44 = 112)
// + month label header (68) + 6 rows × (daySize 46 + weekMargin 6 = 52) = 492.
const CALENDAR_HEIGHT = 492;

const styles = StyleSheet.create({
  container: {
    width: 682,
  },
  containerMobile: {
    width: "100%",
  },
  displayRow: {
    flexDirection: "row",
    gap: 16,
    width: 610,
    alignSelf: "center",
    marginBottom: 24,
  },
  displayRowMobile: {
    width: "100%",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: 16,
  },
  displayBox: {
    width: 297,
    height: 64,
    backgroundColor: "#f6f7f9",
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: "#d8dbe1",
  },
  displayBoxMobile: {
    flex: 1,
    minWidth: 140,
    height: 62,
  },
  displayText: {
    fontSize: 32,
    lineHeight: 40,
    fontWeight: "400",
    color: "#d63c8a",
  },
  displayTextMobile: {
    fontSize: 24,
    lineHeight: 32,
  },
  pickerContainerBase: {
    backgroundColor: "#fff",
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#d8dbe1",
  },
  pickerContainerDesktop: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 16,
    padding: 24,
    width: 682,
    // Height grows to fit the calendar (up to a 6-row month) instead of a fixed
    // value that would clip the last week rows.
    minHeight: 476,
  },
  pickerContainerMobile: {
    width: "100%",
    flexDirection: "column",
    gap: 16,
  },
  calendarSection: {
    width: 328,
    height: CALENDAR_HEIGHT,
  },
  calendarSectionMobile: {
    width: "100%",
    height: CALENDAR_HEIGHT,
  },
  timeSection: {
    flexDirection: "row",
    width: 297,
    height: 255,
    borderLeftWidth: 4,
    borderLeftColor: "#d8dbe1",
    paddingLeft: 20,
  },
  timeSectionMobile: {
    width: "100%",
    borderLeftWidth: 0,
    borderTopWidth: 1,
    borderTopColor: "#d8dbe1",
    paddingLeft: 0,
    paddingTop: 56,
    marginTop: 12,
    height: undefined,
  },
  timeScroll: {
    flex: 1,
    maxHeight: 255,
  },
  timeScrollMobile: {
    flex: 0,
    height: 160,
  },
  timeScrollContent: {
    alignItems: "center",
    paddingVertical: 8,
  },
  timeOption: {
    height: 40,
    minWidth: 112,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 8,
    marginBottom: 4,
    paddingHorizontal: 8,
  },
  timeOptionSelected: {
    backgroundColor: "#d63c8a",
  },
  timeOptionText: {
    fontSize: 16,
    color: "#2b2b2f",
  },
  timeOptionTextSelected: {
    color: "#fff",
    fontWeight: "700",
  },
  emptyTimeState: {
    flex: 1,
    minHeight: 120,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 16,
  },
  emptyTimeText: {
    color: "#686a73",
    fontSize: 14,
    lineHeight: 20,
    textAlign: "center",
  },
});
